import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, tooltipClasses, Box, Typography, TypographyProps } from '@mui/material';

export interface HeaderTooltipProps {
  title: string | React.ReactElement;
  popperTextVariant?: TypographyProps['variant'];
  textVariant?: TypographyProps['variant'];
  dataTestId?: string;
}

const MAX_POPPER_HEIGHT = 300;
const MAX_POPPER_WIDTH = 700;
const ENTER_DELAY = 600;
const LEAVE_DELAY = 400;

export const HeaderTooltip: React.FC<HeaderTooltipProps> = ({
  title,
  dataTestId,
  textVariant,
  popperTextVariant,
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  // Whether the text is clamped depends on the width it renders at, so it cannot
  // be known during render: measure after paint, and again whenever the element
  // resizes. Otherwise the tooltip stays disabled and the full value is
  // unreachable once the text is truncated. (OCST-33462)
  useEffect(() => {
    const element = textRef.current;

    if (!element) {
      return undefined;
    }

    const measure = () => setIsClamped(element.clientHeight < element.scrollHeight);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [title]);

  return (
    <Tooltip
      enterDelay={ENTER_DELAY}
      leaveDelay={LEAVE_DELAY}
      placement='bottom-start'
      disableHoverListener={!isClamped}
      componentsProps={{
        popper: {
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              backgroundColor: (theme) => theme.palette.common.white,
              color: (theme) => theme.palette.text.primary,
              boxShadow: (theme) => theme.shadows[10],
              maxWidth: MAX_POPPER_WIDTH,
              py: 2,
              px: 0,
            },
          },
        },
      }}
      title={
        <Box overflow='auto' maxHeight={MAX_POPPER_HEIGHT} px={2}>
          <Typography variant={popperTextVariant}>{title}</Typography>
        </Box>
      }
    >
      <Typography
        ref={textRef}
        color='primary.contrastText'
        variant={textVariant}
        datatest-id={dataTestId}
        sx={{
          overflow: 'hidden',
          display: '-webkit-box',
          wordBreak: 'break-word',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {title}
      </Typography>
    </Tooltip>
  );
};
